import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    LinearProgress,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider
} from '@mui/material';
import {
    IconFile,
    IconFileZip,
    IconPhoto,
    IconVideo,
    IconFileText,
    IconFileSpreadsheet,
    IconFileCode
} from '@tabler/icons-react';
import ReactApexChart from 'react-apexcharts';

const StorageAnalytics = () => {
    const theme = useTheme();

    // Storage by file type chart
    const storageChartOptions = {
        chart: {
            type: 'donut',
            height: 350
        },
        labels: ['Documents', 'Images', 'Videos', 'Archives', 'Code', 'Others'],
        colors: ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#607D8B'],
        legend: {
            position: 'bottom'
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val.toFixed(1) + '%';
            }
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        width: 200
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }
        ]
    };

    const storageChartSeries = [25.3, 18.7, 15.2, 12.8, 10.5, 17.5];

    // Upload trends chart
    const uploadTrendsOptions = {
        chart: {
            type: 'area',
            height: 250,
            sparkline: {
                enabled: true
            }
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [50, 100, 100, 100]
            }
        },
        colors: [theme.palette.primary.main],
        tooltip: {
            fixed: {
                enabled: false
            },
            x: {
                show: false
            },
            y: {
                title: {
                    formatter: function () {
                        return 'Files: ';
                    }
                }
            },
            marker: {
                show: false
            }
        }
    };

    const uploadTrendsSeries = [
        {
            name: 'Uploads',
            data: [45, 52, 38, 45, 19, 23, 28, 35, 41, 45, 52, 58]
        }
    ];

    // File type statistics
    const fileTypes = [
        { type: 'Documents', icon: IconFileText, count: 3247, size: '4.2 GB', color: '#2196F3' },
        { type: 'Images', icon: IconPhoto, count: 2156, size: '3.1 GB', color: '#4CAF50' },
        { type: 'Videos', icon: IconVideo, count: 892, size: '2.5 GB', color: '#FF9800' },
        { type: 'Archives', icon: IconFileZip, count: 645, size: '2.1 GB', color: '#F44336' },
        { type: 'Code Files', icon: IconFileCode, count: 1523, size: '1.7 GB', color: '#9C27B0' },
        { type: 'Spreadsheets', icon: IconFileSpreadsheet, count: 423, size: '890 MB', color: '#00BCD4' }
    ];

    // Recent activity
    const recentActivity = [
        { file: 'Q4_Report_2024.pdf', action: 'uploaded', user: 'John Doe', time: '2 mins ago', size: '2.4 MB' },
        { file: 'product_images.zip', action: 'downloaded', user: 'Jane Smith', time: '15 mins ago', size: '145 MB' },
        { file: 'presentation_v3.pptx', action: 'modified', user: 'Mike Johnson', time: '1 hour ago', size: '8.7 MB' },
        { file: 'dataset_analysis.csv', action: 'deleted', user: 'Sarah Wilson', time: '2 hours ago', size: '56 MB' },
        { file: 'project_backup.tar.gz', action: 'uploaded', user: 'David Brown', time: '3 hours ago', size: '1.2 GB' }
    ];

    const getActionColor = (action) => {
        switch (action) {
            case 'uploaded':
                return 'success';
            case 'downloaded':
                return 'info';
            case 'modified':
                return 'warning';
            case 'deleted':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Grid container spacing={3}>
            {/* Storage Distribution */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Storage Distribution
                        </Typography>
                        <ReactApexChart options={storageChartOptions} series={storageChartSeries} type="donut" height={350} />
                    </CardContent>
                </Card>
            </Grid>

            {/* File Type Statistics */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            File Type Statistics
                        </Typography>
                        <List>
                            {fileTypes.map((fileType, index) => {
                                const Icon = fileType.icon;
                                return (
                                    <React.Fragment key={index}>
                                        <ListItem>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: fileType.color }}>
                                                    <Icon size={20} />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText primary={fileType.type} secondary={`${fileType.count.toLocaleString()} files`} />
                                            <Typography variant="h6" color="textSecondary">
                                                {fileType.size}
                                            </Typography>
                                        </ListItem>
                                        {index < fileTypes.length - 1 && <Divider variant="inset" component="li" />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </CardContent>
                </Card>
            </Grid>

            {/* Upload Trends */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                                <Typography variant="h4">Upload Trends</Typography>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Last 12 months
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="h3" color="primary">
                                    +23.5%
                                </Typography>
                                <Typography variant="subtitle2" color="textSecondary">
                                    vs last year
                                </Typography>
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                            <ReactApexChart options={uploadTrendsOptions} series={uploadTrendsSeries} type="area" height={250} />
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Recent Activity
                        </Typography>
                        <List>
                            {recentActivity.map((activity, index) => (
                                <React.Fragment key={index}>
                                    <ListItem>
                                        <ListItemAvatar>
                                            <Avatar>
                                                <IconFile size={20} />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="subtitle2">{activity.file}</Typography>
                                                    <Chip label={activity.action} size="small" color={getActionColor(activity.action)} />
                                                </Box>
                                            }
                                            secondary={
                                                <Box display="flex" justifyContent="space-between" mt={0.5}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        by {activity.user} • {activity.time}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {activity.size}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < recentActivity.length - 1 && <Divider variant="inset" component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            </Grid>

            {/* Storage Quota */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h4" gutterBottom>
                            Storage Quota Usage
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2">Used Storage</Typography>
                                <Typography variant="body2">75.8 GB / 100 GB</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={75.8}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 5,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.error.main} 100%)`
                                    }
                                }}
                            />
                            <Box display="flex" justifyContent="space-between" mt={1}>
                                <Typography variant="caption" color="textSecondary">
                                    75.8% used
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    24.2 GB available
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default StorageAnalytics;
